export interface SseFrame {
  event: string;
  data: string;
}

/**
 * Parse a ReadableStream<Uint8Array> emitting SSE-formatted bytes into
 * a stream of typed { event, data } frames.
 *
 * Protocol per https://html.spec.whatwg.org/multipage/server-sent-events.html:
 * - Each frame ends with a blank line (\n\n)
 * - Lines starting with `event:` set the event name
 * - Lines starting with `data:` are concatenated with \n
 * - Lines starting with `:` are comments (ignored)
 *
 * Streaming-safe: TextDecoder with stream:true handles chunk boundaries
 * inside multi-byte UTF-8 sequences.
 */
export async function* parseSseStream(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<SseFrame> {
  const reader = stream.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) >= 0) {
        const rawFrame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const frame = parseFrame(rawFrame);
        if (frame) yield frame;
      }
    }

    if (buffer.trim().length > 0) {
      const frame = parseFrame(buffer);
      if (frame) yield frame;
    }
  } finally {
    reader.releaseLock();
  }
}

function parseFrame(raw: string): SseFrame | null {
  let eventName = '';
  const dataLines: string[] = [];

  for (const line of raw.split('\n')) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  if (!eventName) return null;
  return { event: eventName, data: dataLines.join('\n') };
}
